import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AxiosResponse } from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { getProductReceiveDailyReportTemplate } from "../../document-templates/ProductReceiveDailyReportTemplate";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { ReportByWarehouseAndPartner } from "../../models/Report";
import { Utility } from "../../core/Utility";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllDocuments } from "../../services/NewVersionService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllProducts } from "../../services/ProductService";
import { getAllPartners } from "../../services/PartnerService";
import { getAllOwnershipDocuments } from "../../services/OwnershipDocumentService";
import "./ProductReceiveDailyReportPage.scss";

export const ProductReceiveDailyReportPage = () => {
  const { t } = useTranslation();
  const { state } = useApplicationContext();

  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = new Date(businessYear, 0, 1);
  const yearEnd = new Date(businessYear, 11, 31);

  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    if (businessYear === today.getFullYear()) {
      const from = new Date(today.getFullYear(), 0, 1);
      const to = today;
      return { from, to };
    } else {
      return { from: yearStart, to: yearEnd };
    }
  };
  const [dateFrom, setDateFrom] = useState<Date>(
    getDefaultRangeForBusinessYear().from
  );
  const [dateTo, setDateTo] = useState<Date>(
    getDefaultRangeForBusinessYear().to
  );

  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");
  const [selectedPartner, setSelectedPartner] = useState<string>("");
  const [changeState, setChangeState] = useState<boolean>(false);
  const [warehousesApi, setWarehousesApi] = useState<any[]>([]);
  const [productsApi, setProductsApi] = useState<any[]>([]);
  const [partnersApi, setPartnersApi] = useState<any[]>([]);
  const [ownershipDocumentsApi, setOwnershipDocumentsApi] = useState<any[]>([]);
  const [dailyMercantileData, setDailyMercantileData] = useState<
    ReportByWarehouseAndPartner[]
  >([]);
  const [totalInputNeto, setTotalInputNeto] = useState<number>(0);
  const [totalInputSrps, setTotalInputSrps] = useState<number>(0);
  const [totalOutputNeto, setTotalOutputNeto] = useState<number>(0);
  const [totalOutputSrps, setTotalOutputSrps] = useState<number>(0);
  const [processing, setProcessing] = useState(false);
  const [dailyRepromaterialData, setDailyRepromaterialData] = useState<
    ReportByWarehouseAndPartner[]
  >([]);
  const [pdfData, setPdfData] = useState<any>(
    getProductReceiveDailyReportTemplate(
      dailyMercantileData,
      dailyRepromaterialData,
      selectedWarehouse,
      selectedPartner,
      totalInputNeto,
      totalInputSrps,
      totalOutputNeto,
      totalOutputSrps
    )
  );
  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookups = async () => {
    const [wResp, prResp, paResp, odResp]: [
      AxiosResponse,
      AxiosResponse,
      AxiosResponse,
      AxiosResponse
    ] = await Promise.all([
      getAllWarehouses(),
      getAllProducts(),
      getAllPartners(),
      getAllOwnershipDocuments(),
    ]);

    [wResp, prResp, paResp, odResp].forEach(ensureSuccess);

    setWarehousesApi(wResp?.data?.result ?? []);
    setProductsApi(prResp?.data?.result ?? []);
    setPartnersApi(paResp?.data?.result ?? []);
    setOwnershipDocumentsApi(odResp?.data?.result ?? []);
  };

  useEffect(() => {
    //get data from server
    refreshLookups()
      .then(() => setChangeState((prev) => !prev))
      .finally(() => setProcessing(false));
  }, []);

  useEffect(() => {
    //reset states on filter change
    setDailyMercantileData([]);
    setDailyRepromaterialData([]);
    setChangeState((prev) => !prev);
  }, [selectedWarehouse, selectedPartner, dateFrom, dateTo]);

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      await formatReportData();
      setProcessing(false);
    };
    run();
  }, [changeState]);

  useEffect(() => {
    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
  }, [businessYear]);

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getProductReceiveDailyReportTemplate(
      dailyMercantileData,
      dailyRepromaterialData,
      warehousesApi.find((item) => item.id === selectedWarehouse)
        ?.name as string,
      selectedPartner === ""
        ? " - sve partnere"
        : (partnersApi.find((item) => item.id === selectedPartner)
            ?.name as string),
      totalInputNeto,
      totalInputSrps,
      totalOutputNeto,
      totalOutputSrps
    );
    setPdfData(newPdf);
  }, [dailyMercantileData, dailyRepromaterialData]);

  const formatReportData = async () => {
    const warehouse = warehousesApi.find(
      (item) => item.id == selectedWarehouse
    );
    const warehouseName = warehouse?.name;
    const partner = partnersApi.find((item) => item.id == selectedPartner);
    const partnerName = partner?.name;

    const dateFromFormated = new Date(dateFrom).getTime();
    const dateToFormated = new Date(dateTo).getTime() + 1000 * 60 * 60 * 24; // kraj dana

    let copyOfDailyMercantileData: ReportByWarehouseAndPartner[] = [];
    let copyOfDailyRepromaterialData: ReportByWarehouseAndPartner[] = [];

    // 1) Povuci ulaze/izlaze merkantile i repromaterijala
    const [
      mercInputsResp,
      mercOutputsResp,
      reproInputsResp,
      reproOutputsResp,
    ]: [AxiosResponse, AxiosResponse, AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllDocuments(true, true, dateFrom, dateTo), // merkantila - prijemnice
        getAllDocuments(true, false, dateFrom, dateTo), // merkantila - otpremnice
        getAllDocuments(false, true, dateFrom, dateTo), // reprom. - prijemnice
        getAllDocuments(false, false, dateFrom, dateTo), // reprom. - otpremnice
      ]);

    const mercantileInputs = mercInputsResp.data.result;
    const mercantileOutputs = mercOutputsResp.data.result;
    const repromaterialInputs = reproInputsResp.data.result;
    const repromaterialOutputs = reproOutputsResp.data.result;

    const matchesWarehouseAndPartner = (doc: any) => {
      if (selectedWarehouse !== "" && doc.warehouseName !== warehouseName)
        return false;
      if (partnerName && doc.partnerName !== partnerName) return false;
      const ts = new Date(doc.date).getTime();
      if (ts <= dateFromFormated || ts > dateToFormated) return false;
      return true;
    };

    const sameDay = (d1: Date, d2: Date) =>
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();

    // 2) MERKANTILA – PRIJEMNICE (ULAZ)
    for (const mercantileInput of mercantileInputs) {
      if (
        mercantileInput?.isDocumentFinished !== true ||
        !matchesWarehouseAndPartner(mercantileInput)
      ) {
        continue;
      }

      const docDate = new Date(mercantileInput.date);
      const productName = mercantileInput.productName as string;

      const index = copyOfDailyMercantileData.findIndex((item) => {
        const d = new Date(item.date);
        return sameDay(d, docDate) && item.productName === productName;
      });

      if (index >= 0) {
        copyOfDailyMercantileData[index].importAmount +=
          mercantileInput.netto ?? 0;
        copyOfDailyMercantileData[index].importAmountSrps =
          (copyOfDailyMercantileData[index].importAmountSrps ?? 0) +
          (mercantileInput.srps ?? 0);
      } else {
        copyOfDailyMercantileData.push({
          id: mercantileInput.id,
          date: docDate,
          productName,
          importAmount: mercantileInput.netto ?? 0,
          importAmountSrps: mercantileInput.srps ?? 0,
          exportAmount: 0,
          exportAmountSrps: 0,
        });
      }
    }

    // 3) MERKANTILA – OTPREMNICE (IZLAZ)
    for (const mercantileOutput of mercantileOutputs) {
      if (
        mercantileOutput?.isDocumentFinished !== true ||
        !matchesWarehouseAndPartner(mercantileOutput)
      ) {
        continue;
      }

      const docDate = new Date(mercantileOutput.date);
      const productName = mercantileOutput.productName as string;

      const index = copyOfDailyMercantileData.findIndex((item) => {
        const d = new Date(item.date);
        return sameDay(d, docDate) && item.productName === productName;
      });

      if (index >= 0) {
        copyOfDailyMercantileData[index].exportAmount +=
          mercantileOutput.netto ?? 0;
        copyOfDailyMercantileData[index].exportAmountSrps =
          (copyOfDailyMercantileData[index].exportAmountSrps ?? 0) +
          (mercantileOutput.srps ?? 0);
      } else {
        copyOfDailyMercantileData.push({
          id: mercantileOutput.id,
          date: docDate,
          productName,
          importAmount: 0,
          importAmountSrps: 0,
          exportAmount: mercantileOutput.netto ?? 0,
          exportAmountSrps: mercantileOutput.srps ?? 0,
        });
      }
    }

    // 4) VLASNIČKI LISTOVI – MERKANTILA (ostaju kao do sada)
    for (const oneOwnershipDocument of ownershipDocumentsApi) {
      if (
        oneOwnershipDocument.mercantile === true &&
        (selectedWarehouse !== ""
          ? oneOwnershipDocument.warehouse.name === warehouseName
          : true) &&
        oneOwnershipDocument.newOwner?.name === partnerName &&
        new Date(oneOwnershipDocument.date as Date).getTime() >
          dateFromFormated &&
        new Date(oneOwnershipDocument.date as Date).getTime() <= dateToFormated
      ) {
        const docDate = new Date(oneOwnershipDocument.date as Date);
        const productName = oneOwnershipDocument.product?.name as string;

        const index = copyOfDailyMercantileData.findIndex((item) => {
          const d = new Date(item.date);
          return sameDay(d, docDate) && item.productName === productName;
        });

        if (index >= 0) {
          copyOfDailyMercantileData[index].importAmount +=
            oneOwnershipDocument?.netto ?? 0;
          copyOfDailyMercantileData[index].importAmountSrps =
            (copyOfDailyMercantileData[index].importAmountSrps ?? 0) +
            (oneOwnershipDocument?.srps ?? 0);
        } else {
          copyOfDailyMercantileData.push({
            id: oneOwnershipDocument.id,
            date: docDate,
            productName,
            importAmount: oneOwnershipDocument.netto ?? 0,
            importAmountSrps: oneOwnershipDocument.srps ?? 0,
            exportAmount: 0,
            exportAmountSrps: 0,
          });
        }
      }
    }

    for (const oneOwnershipDocument of ownershipDocumentsApi) {
      if (
        oneOwnershipDocument.mercantile === true &&
        (selectedWarehouse !== ""
          ? oneOwnershipDocument.warehouse.name === warehouseName
          : true) &&
        oneOwnershipDocument.previousOwner?.name === partnerName &&
        new Date(oneOwnershipDocument.date as Date).getTime() >
          dateFromFormated &&
        new Date(oneOwnershipDocument.date as Date).getTime() <= dateToFormated
      ) {
        const docDate = new Date(oneOwnershipDocument.date as Date);
        const productName = oneOwnershipDocument.product?.name as string;

        const index = copyOfDailyMercantileData.findIndex((item) => {
          const d = new Date(item.date);
          return sameDay(d, docDate) && item.productName === productName;
        });

        if (index >= 0) {
          copyOfDailyMercantileData[index].exportAmount +=
            oneOwnershipDocument?.netto ?? 0;
          copyOfDailyMercantileData[index].exportAmountSrps =
            (copyOfDailyMercantileData[index].exportAmountSrps ?? 0) +
            (oneOwnershipDocument?.srps ?? 0);
        } else {
          copyOfDailyMercantileData.push({
            id: oneOwnershipDocument.id,
            date: docDate,
            productName,
            importAmount: 0,
            importAmountSrps: 0,
            exportAmount: oneOwnershipDocument.netto ?? 0,
            exportAmountSrps: oneOwnershipDocument.srps ?? 0,
          });
        }
      }
    }

    // 5) REPROMATERIJAL – helper za parsiranje reproProducts/reproAmounts
    const accumulateRepro = (
      doc: any,
      sign: 1 | -1,
      target: ReportByWarehouseAndPartner[]
    ) => {
      if (doc?.isDocumentFinished !== true || !matchesWarehouseAndPartner(doc))
        return;

      const docDate = new Date(doc.date);
      const productIds = (doc.reproProducts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");
      const amounts = (doc.reproAmounts ?? "")
        .split(";")
        .map((x: string) => x.trim())
        .filter((x: string) => x !== "");

      productIds.forEach((prodId: string, idx: number) => {
        const rawAmount = amounts[idx] ?? "0";
        const amount = parseFloat(rawAmount) * sign || 0;

        const index = target.findIndex((item) => {
          const d = new Date(item.date);
          return (
            sameDay(d, docDate) && item.id?.toString() === prodId.toString()
          );
        });

        const productName =
          productsApi.find((p) => p.id?.toString() === prodId.toString())
            ?.name ?? "";

        if (index >= 0) {
          if (sign === 1) {
            target[index].importAmount += amount;
          } else {
            target[index].exportAmount += amount;
          }
        } else {
          target.push({
            id: prodId,
            date: docDate,
            productName,
            importAmount: sign === 1 ? amount : 0,
            exportAmount: sign === -1 ? amount : 0,
          });
        }
      });
    };

    // 6) REPROMATERIJAL – PRIJEMNICE
    for (const repromaterialInput of repromaterialInputs) {
      accumulateRepro(repromaterialInput, 1, copyOfDailyRepromaterialData);
    }

    // 7) REPROMATERIJAL – OTPREMNICE
    for (const repromaterialOutput of repromaterialOutputs) {
      accumulateRepro(repromaterialOutput, -1, copyOfDailyRepromaterialData);
    }

    // 8) VLASNIČKI LISTOVI – REPROMATERIJAL
    for (const oneOwnershipDocument of ownershipDocumentsApi) {
      if (
        oneOwnershipDocument.mercantile === false &&
        (selectedWarehouse !== ""
          ? oneOwnershipDocument.warehouse.name === warehouseName
          : true) &&
        oneOwnershipDocument.newOwner?.name === partnerName &&
        new Date(oneOwnershipDocument.date as Date).getTime() >
          dateFromFormated &&
        new Date(oneOwnershipDocument.date as Date).getTime() <= dateToFormated
      ) {
        const newReproProducts = JSON.parse(
          oneOwnershipDocument?.repromaterialProductsJSON as string
        );
        for (const productItem of newReproProducts) {
          const docDate = new Date(oneOwnershipDocument.date as Date);
          const index = copyOfDailyRepromaterialData.findIndex((item) => {
            const d = new Date(item.date);
            return (
              sameDay(d, docDate) &&
              item.id?.toString() === productItem.id?.toString()
            );
          });
          const productName =
            productsApi.find((p) => p.id == productItem.id)?.name ?? "";
          const amount = parseFloat(productItem.amount as string) || 0;

          if (index >= 0) {
            copyOfDailyRepromaterialData[index].importAmount += amount;
          } else {
            copyOfDailyRepromaterialData.push({
              id: productItem.id,
              date: docDate,
              productName,
              importAmount: amount,
              exportAmount: 0,
            });
          }
        }
      }
    }

    for (const oneOwnershipDocument of ownershipDocumentsApi) {
      if (
        oneOwnershipDocument.mercantile === false &&
        (selectedWarehouse !== ""
          ? oneOwnershipDocument.warehouse.name === warehouseName
          : true) &&
        oneOwnershipDocument.previousOwner?.name === partnerName &&
        new Date(oneOwnershipDocument.date as Date).getTime() >
          dateFromFormated &&
        new Date(oneOwnershipDocument.date as Date).getTime() <= dateToFormated
      ) {
        const newReproProducts = JSON.parse(
          oneOwnershipDocument?.repromaterialProductsJSON as string
        );
        for (const productItem of newReproProducts) {
          const docDate = new Date(oneOwnershipDocument.date as Date);
          const index = copyOfDailyRepromaterialData.findIndex((item) => {
            const d = new Date(item.date);
            return (
              sameDay(d, docDate) &&
              item.id?.toString() === productItem.id?.toString()
            );
          });
          const productName =
            productsApi.find((p) => p.id == productItem.id)?.name ?? "";
          const amount = parseFloat(productItem.amount as string) || 0;

          if (index >= 0) {
            copyOfDailyRepromaterialData[index].exportAmount += amount;
          } else {
            copyOfDailyRepromaterialData.push({
              id: productItem.id,
              date: docDate,
              productName,
              importAmount: 0,
              exportAmount: amount,
            });
          }
        }
      }
    }

    // 9) Sort + ukupni zbirovi
    copyOfDailyMercantileData.sort((a, b) => (a.date < b.date ? 1 : -1));
    copyOfDailyRepromaterialData.sort((a, b) => (a.date < b.date ? 1 : -1));

    let newTotalInputNeto = 0;
    let newTotalInputSrps = 0;
    let newTotalOutputNeto = 0;
    let newTotalOutputSrps = 0;

    for (const item of copyOfDailyMercantileData) {
      newTotalInputNeto += item.importAmount;
      newTotalInputSrps += item.importAmountSrps ?? 0;
      newTotalOutputNeto += item.exportAmount;
      newTotalOutputSrps += item.exportAmountSrps ?? 0;
    }

    setTotalInputNeto(newTotalInputNeto);
    setTotalInputSrps(newTotalInputSrps);
    setTotalOutputNeto(newTotalOutputNeto);
    setTotalOutputSrps(newTotalOutputSrps);

    setDailyMercantileData(copyOfDailyMercantileData);
    setDailyRepromaterialData(copyOfDailyRepromaterialData);
  };

  const onSelectedWarehouseChange = (_property: string, value: string) => {
    setSelectedWarehouse(value);
  };

  const onSelectedPartnerChange = (_property: string, value: string) => {
    setSelectedWarehouse("");
    setSelectedPartner(value);
  };

  const onDateFromChange = (_property: string, value: Date | null) => {
    setDateFrom(value as Date);
  };

  const onDateToChange = (_property: string, value: Date | null) => {
    setDateTo(value as Date);
  };

  const renderMercantileHeader = () => {
    return (
      <div className="mercantile-header">
        <span>{t("MERKANTILNA ROBA")}</span>
      </div>
    );
  };

  const renderRepromaterialHeader = () => {
    return (
      <div className="repromaterial-header">
        <span>{t("REPROMATERIJAL")}</span>
      </div>
    );
  };

  const importAmount = (rowData: ReportByWarehouseAndPartner) => {
    return Utility.addThousandSeparator(rowData.importAmount);
  };

  const exportAmount = (rowData: ReportByWarehouseAndPartner) => {
    return Utility.addThousandSeparator(rowData.exportAmount);
  };

  const formatDate = (rowData: any) => {
    return Utility.formatDate(new Date(rowData.date));
  };

  const formatProductName = (rowData: any) => {
    return productsApi.find((item) => item.id === rowData.id)?.name;
  };

  const importReproAmount = (rowData: any) => {
    return !Number.isInteger(rowData.importAmount)
      ? rowData.importAmount.toFixed(3)
      : rowData.importAmount.toFixed(0);
  };

  const exportReproAmount = (rowData: any) => {
    return !Number.isInteger(rowData.exportAmount)
      ? rowData.exportAmount.toFixed(3)
      : rowData.exportAmount.toFixed(0);
  };

  const {
    control,
    formState: { errors },
  } = useForm({
    mode: "all",
  });

  return (
    <PageLayout>
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-file-text header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
                transform: "translate(0, 5px)",
              }}
            ></i>
            <span className="header-title">
              {t("Pregled ulaza/izlaza robe po danima za partnera")}
            </span>
            <DropdownInput
              id="selectedPartner"
              value={selectedPartner}
              onValueChange={onSelectedPartnerChange}
              options={partnersApi}
              filter={true}
              optionValue="id"
              optionLabel="name"
              labelText=""
              placeholderText={t("Partner")}
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{t("u")}</span>
            <DropdownInput
              id="selectedWarehouse"
              value={selectedWarehouse}
              onValueChange={onSelectedWarehouseChange}
              options={warehousesApi.filter((item) => item.virtual !== true)}
              optionValue="id"
              optionLabel="name"
              labelText=""
              placeholderText={t("Magacin")}
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{t("od")}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{t("do")}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (downloadPdfDocument.current) {
                  // @ts-ignore
                  downloadPdfDocument.current();
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (printPdfDocument.current) {
                  // @ts-ignore
                  printPdfDocument.current();
                }
              }}
            />
          </div>
        </div>
        <div className="table-body">
          {processing ? (
            <ProgressSpinner
              style={{ width: "100px", height: "100px", color: "green" }}
              strokeWidth="4"
              animationDuration=".8s"
            />
          ) : (
            <DataTable
              header={renderMercantileHeader}
              value={dailyMercantileData}
              showGridlines
              stripedRows
            >
              <Column
                body={formatDate}
                header={`${t("Datum")}`}
                footer={`${t("Ukupno")}`}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                field="productName"
                header={`${t("Roba")}`}
                footer=""
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={importAmount}
                header={`${t("Ulaz robe")}`}
                footer={Utility.addThousandSeparator(totalInputNeto)}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
              <Column
                body={exportAmount}
                header={`${t("Izlaz robe")}`}
                footer={Utility.addThousandSeparator(totalOutputNeto)}
                footerStyle={{
                  backgroundColor: "#6C757D",
                  color: "#FFFFFF",
                  border: "none",
                }}
              />
            </DataTable>
          )}
        </div>
        <div className="table-body">
          {!processing && (
            <DataTable
              header={renderRepromaterialHeader}
              value={dailyRepromaterialData}
              showGridlines
              stripedRows
            >
              <Column body={formatDate} header={t("Datum")} />
              <Column body={formatProductName} header={t("Roba")} />
              <Column body={importReproAmount} header={t("Ulaz robe")} />
              <Column body={exportReproAmount} header={t("Izlaz robe")} />
            </DataTable>
          )}
        </div>
        <div className="document-preview">
          <PdfPreview
            pdfDefinition={pdfData}
            downloadDocument={downloadPdfDocument}
            printDocument={printPdfDocument}
            documentName={`${
              selectedWarehouse === ""
                ? t("Svi magacini")
                : warehousesApi.find((item) => item.id === selectedWarehouse)
                    ?.name
            } ${t("za")} ${
              selectedPartner === ""
                ? t(" sve partnere")
                : partnersApi.find((item) => item.id === selectedPartner)?.name
            } ${t("ulaz/izlaz po danima")} ${Utility.formatDate(
              new Date()
            )}.pdf`}
          />
        </div>
      </div>
    </PageLayout>
  );
};
